import { memo, useMemo } from 'react';

import { type Account } from '@air/api/types';
import { Avatar } from '@air/primitive-avatar';
import { formatFullName, formatInitials } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { type BoundingBoxAvatarDirection } from '~/components/BoundingBox/types';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

export type BoundingBoxAvatarProps = {
  account?: Pick<Account, 'id' | 'firstName' | 'lastName' | 'avatar'> | null;
  /** on which side the bottom corner is not rounded */
  direction: BoundingBoxAvatarDirection;
  className?: string;
};

export const BOUNDING_BOX_AVATAR_WIDTH = 24;
// 10 is: 4px padding  + 1px border on left and right
export const BOUNDING_BOX_AVATAR_SIZE = BOUNDING_BOX_AVATAR_WIDTH + 10;

export const BoundingBoxAvatar = memo(({ account, direction, className }: BoundingBoxAvatarProps) => {
  const fullName = account ? formatFullName(account.firstName, account.lastName) : '';
  const initials = account ? formatInitials(account.firstName, account.lastName) : '';

  const borderRadiusStyle = useMemo(() => {
    const borderRadius = {
      borderBottomLeftRadius: 30,
      borderTopRightRadius: 30,
      borderTopLeftRadius: 30,
      borderBottomRightRadius: 30,
    };

    switch (direction) {
      case 'top-left':
        borderRadius.borderTopLeftRadius = 2;
        break;
      case 'top-right':
        borderRadius.borderTopRightRadius = 2;
        break;
      case 'bottom-left':
        borderRadius.borderBottomLeftRadius = 2;
        break;
      case 'bottom-right':
        borderRadius.borderBottomRightRadius = 2;
        break;
    }

    return borderRadius;
  }, [direction]);

  return (
    <div
      className={cn(
        'pointer-events-none border ads:border-ads-gray-1 light:border-gray-1 ads:bg-subtle light:bg-gray-3 p-1 shadow-popover dark:border-gray-12 dark:bg-gray-12',
        className,
      )}
      style={{
        width: BOUNDING_BOX_AVATAR_SIZE,
        height: BOUNDING_BOX_AVATAR_SIZE,
        ...borderRadiusStyle,
      }}
    >
      <Avatar
        alt={fullName}
        size={BOUNDING_BOX_AVATAR_WIDTH}
        src={getAvatarImageSrc(account?.avatar, BOUNDING_BOX_AVATAR_WIDTH)}
        text={initials}
        colorSeed={account?.id}
      />
    </div>
  );
});

BoundingBoxAvatar.displayName = 'BoundingBoxAvatar';
